import React, { useState, useEffect, useRef } from "react";

import { useIntl } from "react-intl";

import ModalConfirmLeaveForm from "Components/ModalConfirmLeaveForm";
import PageMeta from "Components/PageMeta";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { getEnvironmentTypeAccessesThunk } from "Reducers/access/environmentTypeAccess/thunks/getEnvironmentTypeAccesses.thunk";
import { getProjectAccessesThunk } from "Reducers/access/projectAccess/thunks/getProjectAccesses.thunk";
import {
  updateEnvironment,
  environmentSelector,
  environmentsAsArraySelector,
  environmentUpdateErrorSelector,
  environmentLoadingSelector
} from "Reducers/environment";
import { updateDeploymentSettings } from "Reducers/environment/deployment";
import { organizationByDescriptionIdSelector } from "Reducers/organization";
import { gitProjectSelector } from "Reducers/project/project";
import { useDeploymentType } from "Reducers/project/project/hooks/useDeploymentType";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import * as S from "./EnvironmentGeneralSettings.styles";
import EnvironmentSettingsLayout from "./EnvironmentSettingsLayout";
import EnvironmentSettingWrapper from "./EnvironmentSettingWrapper";
import {
  BasicSetting,
  EmailSetting,
  HttpAccessSetting,
  IndexingSetting,
  StatusSetting,
  PausedEnvSettings,
  DeploymentTypeSetting
} from "./SettingsOptions";
import { loginDefault } from "./util";

import type { LoginDefault, LoginFormTypes } from "./types";
import type { Environment } from "@packages/client";

type EnvironmentGeneralSettingsProps = {
  organizationId: string;
  projectId: string;
  environmentId: string;
};

type PermissionsType = "allow" | "deny";

const usePrevious = <T,>(value: T) => {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

const EnvironmentGeneralSettings = ({
  organizationId,
  projectId,
  environmentId
}: EnvironmentGeneralSettingsProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const environment = useSelectorWithUrlParams(environmentSelector);
  const prevEnvironment = usePrevious(environment);

  const organization = useAppSelector(state =>
    organizationByDescriptionIdSelector(state, {
      organizationDescriptionId: organizationId
    })
  );

  const project = useSelectorWithUrlParams(gitProjectSelector);
  const environments = useSelectorWithUrlParams(environmentsAsArraySelector);
  const updateError = useSelectorWithUrlParams(environmentUpdateErrorSelector);
  const isLoading = useSelectorWithUrlParams(environmentLoadingSelector);

  const [confirmDeactivateModal, setConfirmDeactivateModal] = useState(false);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
  const [, setUpdatePlanModal] = useState(false);
  const [environmentType, setEnvironmentType] = useState<string | undefined>(
    ""
  );
  const [is_enabled, setIs_enabled] = useState(false);
  const [addresses, setAddresses] = useState<
    | {
        address: string;
        permission: PermissionsType;
      }[]
    | undefined
  >([]);
  const [basic_auth, setBasic_auth] = useState<
    Environment["http_access"]["basic_auth"]
  >({});

  const [enable_smtp, setEnable_smtp] = useState(false);
  const [restrict_robots, setRestrict_robots] = useState(false);
  const [environmentParent, setEnvironmentParent] = useState<string | null>("");
  const [environmentTitle, setEnvironmentTitle] = useState<string | undefined>(
    ""
  );
  const [isChanged, setIsChanged] = useState(false);
  const [isSectionChanged, setIsSectionChanged] = useState(false);
  const [logins, setLogins] = useState<LoginDefault[]>([]);
  const [removedLogins, setRemovedLogins] = useState<Record<string, boolean>>(
    {}
  );
  const [isModalLeaveOpen, setIsModalLeaveOpen] = useState(false);
  const [ipText, setIpText] = useState<string>("");
  const [editedLine, setEditLine] = useState("");
  const [isAccessSettingsChanged, setIsAccessSettingsChanged] = useState(false);
  const [savedSection, setSavedSection] = useState("");
  const [isEnabled, setIsEnabled] = useState(false);

  const [isAutomaticDeployment, setIsAutomaticDeployment] = useState(false);
  const [isDeploymentTypeChanged, setIsDeploymentTypeChanged] = useState(false);
  const [
    confirmDeploymentTypeChangeModal,
    setConfirmDeploymentTypeChangeModal
  ] = useState(false);

  const {
    shouldShowDeploymentType,
    isAutomaticDeploymentEnabled,
    hasStagedActivities,
    isDeploymentSettingsLoading,
    updateDeploymentSettingsError
  } = useDeploymentType();

  useEffect(() => {
    setIsAutomaticDeployment(isAutomaticDeploymentEnabled);
  }, [isAutomaticDeploymentEnabled]);

  const isHttpEnabled = environment => {
    const basicAuth = environment.http_access.basic_auth ?? {};
    return (
      !!environment.http_access.is_enabled &&
      (Object.keys(basicAuth)?.length !== 0 ||
        environment.http_access.addresses?.length !== 0)
    );
  };

  useEffect(() => {
    if (project?.id) {
      dispatch(getEnvironmentTypeAccessesThunk({ projectId }));
      dispatch(getProjectAccessesThunk({ project, organizationId }));
    }
  }, [dispatch, organizationId, project, projectId]);

  const currentIpAddressWithPermString =
    environment?.http_access.addresses
      ?.map(address => `${address.address} ${address.permission}`)
      .join("\n") ?? "";

  const checkLoginsValidation = (field: LoginFormTypes, index: number) => {
    setLogins(prevState => {
      const currentLogins = [...prevState];

      currentLogins.forEach((loginRow, loginIndex) => {
        ["login", "password"].forEach(fieldType => {
          if (typeof index === "number" && loginIndex === index) {
            if (field && field === fieldType) {
              const fieldValidation = loginRow[fieldType].validation;
              const fieldValue = loginRow[fieldType].value;

              loginRow[fieldType].isValid = fieldValidation.test(fieldValue);
              loginRow[fieldType].isTouched = true;
            }
          }
        });
      });
      return currentLogins;
    });
  };

  const checkHttpLoginValidity = () => {
    let areAllLoginsValid = true;

    logins.forEach(loginRow => {
      ["login", "password"].forEach(fieldType => {
        const fieldValidation = loginRow[fieldType].validation;
        const fieldValue = loginRow[fieldType].value;

        areAllLoginsValid =
          areAllLoginsValid && fieldValidation.test(fieldValue);
      });
    });
    return areAllLoginsValid;
  };

  const areHttpLoginsValid = checkHttpLoginValidity();

  const handleSubmitHttpSection = () => {
    updateEnvironmentSubmit({
      http_access: { is_enabled, addresses, basic_auth }
    });
    setSavedSection("httpAccess");
  };

  useEffect(() => {
    if (environment) {
      const basic_auth = environment.http_access.basic_auth ?? {};
      setLogins(prevLogins => {
        const comingLogins = Object.entries(basic_auth).map(([k, v]) => ({
          login: { ...loginDefault.login, value: k ?? "" },
          password: { ...loginDefault.password, value: v ?? "" }
        }));

        return comingLogins.length === prevLogins?.length
          ? Object.entries(basic_auth).map(([k, v]) => ({
              login: { ...loginDefault.login, value: k ?? "" },
              password: { ...loginDefault.password, value: v ?? "" }
            }))
          : prevLogins;
      });

      setEnvironmentTitle(environment.title);
      setEnvironmentParent(environment.parent);
      setEnvironmentType(environment.type);
      setIs_enabled(isHttpEnabled(environment));
      setAddresses(environment.http_access.addresses);
      setBasic_auth(basic_auth);
      setEnable_smtp(environment.enable_smtp);
      setRestrict_robots(environment.restrict_robots);
      setIpText(currentIpAddressWithPermString);
    }
  }, [environment, currentIpAddressWithPermString]);

  useEffect(() => {
    if (environment?.title && prevEnvironment?.title !== environment?.title) {
      const basic_auth = environment.http_access.basic_auth ?? {};

      setLogins(
        Object.entries(basic_auth).map(([k, v]) => ({
          login: { ...loginDefault.login, value: k ?? "" },
          password: { ...loginDefault.password, value: v ?? "" }
        }))
      );

      setEnvironmentTitle(environment.title);
      setEnvironmentParent(environment.parent);
      setEnvironmentType(environment.type);
      setIs_enabled(isHttpEnabled(environment));
      setAddresses(environment.http_access.addresses);
      setBasic_auth(basic_auth);
      setEnable_smtp(environment.enable_smtp);
      setRestrict_robots(environment.restrict_robots);
      setIsChanged(false);
      setIsModalLeaveOpen(false);
      setIsSectionChanged(false);
    }
  }, [environment, prevEnvironment]);

  const openDeleteConfirm = () => {
    setConfirmDeleteModal(true);
  };

  const openDeactivateConfirm = () => {
    setConfirmDeactivateModal(true);
  };

  const closeModal = () => {
    setIsModalLeaveOpen(false);
    setConfirmDeactivateModal(false);
    setConfirmDeleteModal(false);
    setUpdatePlanModal(false);
    setConfirmDeploymentTypeChangeModal(false);
  };

  const openConfirmDeploymentTypeChangeModal = () => {
    setConfirmDeploymentTypeChangeModal(true);
  };
  const onEnvironmentTitleChange = e => {
    setEditLine("basic");
    const environmentTitle = e.target.value;
    setEnvironmentTitle(environmentTitle);
    setIsSectionChanged(true);
  };

  const onEnvironmentParentChange = ({ value }) => {
    setEditLine("basic");
    setEnvironmentParent(value);
    setIsSectionChanged(true);
  };

  const onEnvironmentTypeChange = ({ value }) => {
    setEditLine("basic");
    setEnvironmentType(value);
    setIsSectionChanged(true);
  };

  const updateEnvironmentSubmit = async (props?: Partial<Environment>) => {
    const http_access = props?.http_access ? props?.http_access : undefined;
    const result = await dispatch(
      updateEnvironment({
        organizationId,
        projectId,
        environmentId,
        data: {
          title: environmentTitle,
          parent: environmentParent,
          http_access,
          enable_smtp,
          restrict_robots,
          type: environmentType,
          ...props
        }
      })
    );

    if (result?.type.includes("rejected")) return;

    setIsChanged(false);
    setIsAccessSettingsChanged(false);
  };
  const onEmailSendingChange = () => {
    setEditLine("email");
    setIsSectionChanged(true);
    setEnable_smtp(prevValue => !prevValue);
  };

  const onIndexingChange = () => {
    setEditLine("indexing");
    setRestrict_robots(prevValue => !prevValue);
    setIsSectionChanged(true);
  };

  const onHttpAccessControlChange = value => {
    setEditLine("httpaccess");
    const { http_access } = environment || {};
    const isValueSameAsInitial = value === http_access?.is_enabled;

    if (isValueSameAsInitial) {
      setIs_enabled(value);
      setIsAccessSettingsChanged(false);
      return;
    }
    const addresses = http_access?.addresses ?? [];
    const basic_auth = http_access?.basic_auth ?? {};

    setIs_enabled(value);
    setAddresses(addresses);
    setBasic_auth(basic_auth);
    setIsChanged(true);
    setIsAccessSettingsChanged(true);
  };

  const textToIp = text => {
    if (!text) {
      return undefined;
    }
    const lines = text.split("\n");

    return lines
      .filter(function (line) {
        return line != "";
      })
      .map(splitedLine => {
        const addressLine = splitedLine.startsWith(" ")
          ? splitedLine.trim()
          : splitedLine;
        const columns = addressLine.split(" ");

        return {
          address: columns[0],
          permission: columns[1]
        };
      });
  };

  const onIpTextAreaChange = (event: React.ChangeEvent) => {
    const value = (event.target as HTMLTextAreaElement).value;
    setEditLine("httpaccess");
    setIpText(value);
    setAddresses(textToIp(value) || []);
    setIsChanged(true);
    setIsEnabled(false);
    setIsAccessSettingsChanged(true);
  };

  const onLoginAddHandler = () => {
    setLogins(prevState => {
      const nextState = [...prevState];
      const loginListCopy = structuredClone(loginDefault);
      nextState.push(loginListCopy);
      return nextState;
    });
  };

  const onLoginChange = ({
    logins,
    removed,
    avoidLoginSave = false
  }: {
    logins: LoginDefault[];
    removed: Record<string, unknown>;
    avoidLoginSave?: boolean;
  }) => {
    const cleanedEntries = logins.map(entry => ({
      login: entry.login.value,
      password: entry.password.value
    }));

    setEditLine("httpaccess");
    let basicAuth: Environment["http_access"]["basic_auth"] = {};
    Object.keys(removed).forEach(login => {
      basicAuth![login] = null;
    });
    Object.keys(basic_auth ?? {}).forEach(login => {
      basicAuth![login] = null;
    });
    basicAuth = cleanedEntries?.reduce((logins, auth) => {
      logins[auth.login] = auth.password;
      return logins;
    }, basicAuth);

    if (!avoidLoginSave) {
      setLogins(logins);
    }
    setBasic_auth(basicAuth);
    setIsChanged(true);
    setIsAccessSettingsChanged(true);
  };

  const removeLineHandler = (index: number) => {
    let copyRemovedLogins = structuredClone(removedLogins);
    if (logins[index]?.login.value) {
      const removedIndex = logins[index]?.login.value;
      copyRemovedLogins = { ...copyRemovedLogins, [`${removedIndex}`]: true };
      setRemovedLogins(copyRemovedLogins);
    }
    const copyLogins = structuredClone(logins);
    copyLogins.splice(index, 1);
    onLoginChange({ logins: copyLogins, removed: copyRemovedLogins });
  };

  const onLoginsChangeHandler = (
    newValue: string,
    field: LoginFormTypes,
    index: number
  ) => {
    setLogins(prevState => {
      const loginState = [...prevState];

      loginState[index][field].value = newValue.trim();
      loginState[index][field].isTouched = true;

      // Update the "isValid" field only if it is false.
      // It's deliberate to not show invalid form errors because the user is interacting
      if (!loginState[index][field].isValid) {
        const fieldValidation = loginState[index][field].validation;
        const fieldValue = loginState[index][field].value;

        loginState[index][field].isValid = fieldValidation.test(fieldValue);
      }

      onLoginChange({
        logins: loginState,
        removed: removedLogins,
        avoidLoginSave: true
      });
      return loginState;
    });
  };

  const onDeploymentTypeChange = value => {
    setEditLine("deploymentType");
    setIsDeploymentTypeChanged(prevValue => !prevValue);
    setIsAutomaticDeployment(value);

    if (value && hasStagedActivities) openConfirmDeploymentTypeChangeModal();
  };

  const onCancelClick = () => {
    setIsModalLeaveOpen(true);
  };

  if (!environment) return null;

  const cancel = () => {
    const basic_auth = environment.http_access.basic_auth ?? {};

    setLogins(
      Object.entries(basic_auth).map(([k, v]) => ({
        login: { ...structuredClone(loginDefault).login, value: k ?? "" },
        password: { ...structuredClone(loginDefault).password, value: v ?? "" }
      }))
    );

    setIpText(currentIpAddressWithPermString);

    setIsSectionChanged(false);
    setEnvironmentTitle(environment.title);
    setEnvironmentType(environment.type);
    setEnvironmentParent(environment.parent ?? null);
    setIs_enabled(isHttpEnabled(environment));
    setAddresses(environment.http_access.addresses);
    setEnable_smtp(environment.enable_smtp);
    setRestrict_robots(environment.restrict_robots);
    setIsModalLeaveOpen(hasStagedActivities ? false : true);
    setIsAccessSettingsChanged(false);
    setIsAutomaticDeployment(isAutomaticDeploymentEnabled);
    setIsDeploymentTypeChanged(false);
  };

  const isEnvActive = environment.isActive?.();

  const parentEnvironments = removeDescendants(environmentId, environments).map(
    env => ({
      value: env.id,
      label: env.title
    })
  );

  const editLine = index => {
    if (isChanged && !isModalLeaveOpen) {
      setIsModalLeaveOpen(true);
    } else {
      if (editedLine === index) {
        setEditLine("");
        setIsChanged(false);
        setIsModalLeaveOpen(false);
      } else {
        setEditLine(index);
        setIsChanged(false);
        setIsModalLeaveOpen(false);
      }
    }
  };
  const isEnvTitleEmpty = environmentTitle === "";

  const onBasicSettingsSave = () => {
    updateEnvironmentSubmit({
      title: environmentTitle,
      parent: environmentParent,
      type: environmentType,
      enable_smtp,
      restrict_robots
    });
    setSavedSection("basic");
    setIsSectionChanged(false);
  };

  const onSectionSave = () => {
    onBasicSettingsSave();
  };
  const onChangeDeploymentTypeSave = e => {
    e.preventDefault();
    setIsDeploymentTypeChanged(false);
    setConfirmDeploymentTypeChangeModal(false);
    dispatch(
      updateDeploymentSettings({
        projectId,
        environmentId,
        organizationId,
        explicitDeploymentsEnabled: !isAutomaticDeployment
      })
    );
  };
  const envStatus = environment?.status;
  const isEnvPaused = envStatus === "paused";
  const canPauseOrResumeEnvironment =
    environment.hasLink("#pause") || environment.hasLink("#resume");

  const isDirtyEnvironment = envStatus === "dirty";
  const isDeletingEnvironment = envStatus === "deleting";
  const isActiveEnvironment = envStatus === "active";
  const hasEditPermission = environment?.hasPermission?.("#edit");
  const isEnvironmentTypeDevelopment = environment.type === "development";
  return (
    <EnvironmentSettingsLayout>
      <PageMeta
        title={`Settings | ${environment?.title} | ${project?.title}`}
      />
      <S.EnvironmentTitle tag="h2" id="settings-heading">
        {environment?.title || intl.formatMessage({ id: "general" })}
      </S.EnvironmentTitle>
      <S.Wrapper>
        {" "}
        <EnvironmentSettingWrapper
          close={() => editLine("")}
          isUpdateLoading={isLoading && savedSection === "basic"}
          save={e => {
            e.preventDefault();
            onSectionSave();
          }}
          savePermission={hasEditPermission}
          itemId="basic"
          cancel={onCancelClick}
          hideButtons={!isSectionChanged || isEnvTitleEmpty}
        >
          <BasicSetting
            environment={environment}
            environmentParent={environmentParent}
            environmentTitle={environmentTitle}
            environmentType={environmentType}
            errors={updateError?.original?.detail}
            onEnvironmentParentChange={onEnvironmentParentChange}
            onEnvironmentTitleChange={onEnvironmentTitleChange}
            onEnvironmentTypeChange={onEnvironmentTypeChange}
            parentEnvironments={parentEnvironments}
          />
          <EmailSetting
            environment={environment}
            enable_smtp={enable_smtp}
            onEmailSendingChange={onEmailSendingChange}
          />
          {environment?.supports_restrict_robots && (
            <IndexingSetting
              environment={environment}
              onIndexingChange={onIndexingChange}
              restrict_robots={restrict_robots}
              organization={organization}
              project={project || undefined}
            />
          )}
        </EnvironmentSettingWrapper>
      </S.Wrapper>{" "}
      {shouldShowDeploymentType && !isEnvironmentTypeDevelopment && (
        <S.Wrapper>
          <EnvironmentSettingWrapper
            isUpdateLoading={isDeploymentSettingsLoading}
            close={() => editLine("")}
            save={
              hasStagedActivities && isAutomaticDeployment
                ? undefined
                : onChangeDeploymentTypeSave
            }
            savePermission={!!environment?.hasPermission?.("#edit")}
            itemId="deploymetType"
            cancel={onCancelClick}
            hideButtons={
              (hasStagedActivities && !isAutomaticDeploymentEnabled) ||
              !isDeploymentTypeChanged
            }
          >
            <DeploymentTypeSetting
              closeModal={closeModal}
              confirmDeploymentTypeChangeModal={
                confirmDeploymentTypeChangeModal
              }
              cancel={cancel}
              disabled={
                !hasEditPermission || environment.type === "development"
              }
              onChangeDeploymentTypeSave={onChangeDeploymentTypeSave}
              environment={environment}
              onDeploymentTypeChange={onDeploymentTypeChange}
              errors={updateDeploymentSettingsError}
              isAutomaticDeployment={isAutomaticDeployment}
            />
          </EnvironmentSettingWrapper>
        </S.Wrapper>
      )}
      <S.Wrapper>
        <EnvironmentSettingWrapper
          close={() => editLine("")}
          isUpdateLoading={isLoading && editedLine === "httpaccess"}
          save={handleSubmitHttpSection}
          savePermission={hasEditPermission}
          incompleteConfigs={!areHttpLoginsValid}
          itemId="httpaccess"
          cancel={onCancelClick}
          hideButtons={
            !isChanged ||
            !isAccessSettingsChanged ||
            isEnabled ||
            isDirtyEnvironment
          }
        >
          {isDirtyEnvironment ? (
            <S.Loading />
          ) : (
            <HttpAccessSetting
              basicAuth={logins}
              environment={environment}
              errors={updateError}
              ipText={ipText}
              isEnabled={is_enabled}
              onHttpAccessControlChange={onHttpAccessControlChange}
              onIpTextAreaChange={onIpTextAreaChange}
              onLoginsChangeHandler={onLoginsChangeHandler}
              onLoginAddHandler={onLoginAddHandler}
              removeLineHandler={removeLineHandler}
              projectId={projectId}
              checkLoginsValidation={checkLoginsValidation}
            />
          )}
        </EnvironmentSettingWrapper>
      </S.Wrapper>
      {canPauseOrResumeEnvironment && (
        <PausedEnvSettings
          isEnvPaused={isEnvPaused}
          isEnvActive={isActiveEnvironment}
          isLoading={isLoading || isDirtyEnvironment || isDeletingEnvironment}
          projectId={projectId}
          environmentId={environmentId}
          organizationId={organizationId}
        />
      )}
      <StatusSetting
        confirmDeactivateModal={confirmDeactivateModal}
        organizationId={organizationId}
        projectId={projectId}
        environmentId={environmentId}
        closeModal={closeModal}
        isEnvActive={isEnvActive || isEnvPaused}
        environment={environment}
        project={project}
        openDeactivateConfirm={openDeactivateConfirm}
        openDeleteConfirm={openDeleteConfirm}
        confirmDeleteModal={confirmDeleteModal}
        organization={organization}
      />
      <section aria-labelledby="settings-heading">
        <ModalConfirmLeaveForm
          isOpen={isModalLeaveOpen}
          closeModal={closeModal}
          continueFunction={cancel}
          cancelFunction={closeModal}
        />
      </section>
    </EnvironmentSettingsLayout>
  );
};

type HashMap = {
  [parent: string]: string[];
};

function findDescendants(id: string, hashMap: HashMap): string[] {
  let descendants: string[] = [];

  if (hashMap[id]) {
    for (const child of hashMap[id]) {
      descendants.push(child);
      descendants = descendants.concat(findDescendants(child, hashMap));
    }
  }

  return descendants;
}

function removeDescendants(id: string, envs: Environment[]): Environment[] {
  // Create a hash map for parent-child relationship
  const hashMap: HashMap = {};
  for (const env of envs) {
    if (!env.parent) {
      continue;
    }
    if (!hashMap[env.parent]) {
      hashMap[env.parent] = [];
    }
    hashMap[env.parent].push(env.id);
  }

  const descendants = findDescendants(id, hashMap);
  // Add the given id to the descendants array
  descendants.push(id);

  // Filter out all the descendants and the given id from the envs array
  return envs.filter(env => !descendants.includes(env.id));
}

export default EnvironmentGeneralSettings;
